import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { DateInputFillMode, DateInputSize } from '@progress/kendo-angular-dateinputs';
import { CheckBoxRounded } from '@progress/kendo-angular-inputs';

import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { SHORT_DEBOUNCE_TIME } from 'src/app/extensions/constants/commonConstants';
import { DictionaryContractType } from 'src/app/extensions/constants/dictionaryConstants/dictionaryConstants';
import { PAYMENT_STATUS } from 'src/app/extensions/constants/filtersConstants';
import { ContractAPIService } from 'src/app/extensions/services/api/contracts-api.service';
import { DictionaryAPIService } from 'src/app/extensions/services/api/dictionary-api.service';
import { InvoiceAPIService } from 'src/app/extensions/services/api/invoice-api.service';
import { isNotEmpty, normalizeString } from 'src/app/extensions/utilities';
import { isEqual } from 'lodash';

@Component({
  selector: 'app-contract-list-filter',
  templateUrl: './contract-list-filter.component.html',
  styleUrl: './contract-list-filter.component.scss',
  standalone: false,
})
export class ContractListFilterComponent implements OnInit {
  public fillModeOutline: DateInputFillMode = 'outline';
  public roundedLarge: CheckBoxRounded = 'large';
  public size: DateInputSize = 'large';
  searchQuery$ = new Subject<string>();

  public contractsFilter: app.models.ContractsFilterModel;
  contractTypes: app.models.DictionaryWithCheckbox[];
  statuses: app.models.DictionaryWithCheckbox[];
  assets: app.models.AssetModel[];

  private partnerExternalEntityId: string | undefined;

  PAYMENT_STATUS = PAYMENT_STATUS;
  filteredAssets: app.models.AssetModel[];
  initialFilterState: app.models.ContractsFilterModel;
  terminationUntilDate: Date;
  assetSearchText: string = '';

  constructor(
    private _activeModal: NgbActiveModal,
    public _invoiceApiService: InvoiceAPIService,
    private _contractsAPIService: ContractAPIService,
    public _translateService: TranslateService,
    public _dictionaryApiService: DictionaryAPIService
  ) {}

  ngOnInit(): void {
    this.loadData();
    this.searchQuery$.pipe(debounceTime(SHORT_DEBOUNCE_TIME)).subscribe(value => {
      let normalizedSearchText = normalizeString(value);
      this.filteredAssets = this.assets.filter(
        x =>
          x.name.toLowerCase().includes(normalizedSearchText) ||
          (x.uid && x.uid.toLowerCase().includes(normalizedSearchText)) ||
          (x.number && x.number.toLowerCase().includes(normalizedSearchText))
      );
    });

    if (this.contractsFilter.terminationUntilDate) {
      this.terminationUntilDate = new Date(this.contractsFilter.terminationUntilDate);
    }
  }

  loadData(searchText: string | undefined = undefined) {
    this._contractsAPIService
      .getContractAssets(undefined, this.contractsFilter.activeContracts, this.partnerExternalEntityId)
      .subscribe(contractsAsset => {
        this.assets = contractsAsset.data.map(x => ({
          ...x,
          isSelected: this.contractsFilter.contractAssetIds.includes(x.assetId),
        }));
        this.filteredAssets = this.assets;
        if (isNotEmpty(searchText)) {
          let normalizedSearchText = normalizeString(searchText);
          this.filteredAssets = this.filteredAssets.filter(
            x =>
              x.name.toLowerCase().includes(normalizedSearchText) ||
              (x.uid && x.uid.toLowerCase().includes(normalizedSearchText)) ||
              (x.number && x.number.toLowerCase().includes(normalizedSearchText))
          );
        }

        this.contractTypes = [
          {
            name: this._translateService.instant('contracts.contractType.operationalLeasing'),
            isSelected: this.contractsFilter.contractTypeCodes.includes(DictionaryContractType.OPERATIONAL_LEASING),
            value: DictionaryContractType.OPERATIONAL_LEASING,
          },
          {
            name: this._translateService.instant('contracts.contractType.financialLeasing'),
            isSelected: this.contractsFilter.contractTypeCodes.includes(DictionaryContractType.FINANCIAL_LEASING),
            value: DictionaryContractType.FINANCIAL_LEASING,
          },
          {
            name: this._translateService.instant('contracts.contractType.credit'),
            isSelected: this.contractsFilter.contractTypeCodes.includes(DictionaryContractType.CREDIT),
            value: DictionaryContractType.CREDIT,
          },
        ];

        this.statuses = [
          {
            name: this._translateService.instant('invoice.status.paid'),
            isSelected: this.contractsFilter.isPaid,
            value: PAYMENT_STATUS.PAID,
          },
          {
            name: this._translateService.instant('invoice.status.notPaid'),
            isSelected: this.contractsFilter.isNotPaid,
            value: PAYMENT_STATUS.NOT_PAID,
          },
          {
            name: this._translateService.instant('invoice.status.overdue'),
            isSelected: this.contractsFilter.isOverdue,
            value: PAYMENT_STATUS.OVERDUE,
          },
        ];
      });

    this.searchQuery$.pipe(debounceTime(SHORT_DEBOUNCE_TIME), distinctUntilChanged()).subscribe(value => {
      let normalizedSearchText = normalizeString(value);
      this.filteredAssets = this.assets.filter(
        x =>
          x.name.toLowerCase().includes(normalizedSearchText) ||
          (x.uid && x.uid.toLowerCase().includes(normalizedSearchText)) ||
          (x.number && x.number.toLowerCase().includes(normalizedSearchText))
      );
    });

    if (this.contractsFilter.terminationUntilDate) {
      this.terminationUntilDate = new Date(this.contractsFilter.terminationUntilDate);
    }
  }

  public filterModalActionClick(applyFilters: boolean = false) {
    if (applyFilters) {
      this.contractsFilter.contractTypeCodes = this.contractTypes.filter(x => x.isSelected).map(x => x.value);
      this.contractsFilter.contractAssetIds = this.assets.filter(x => x.isSelected).map(x => x.assetId);
      this.statuses.map(status => {
        if (status.value === PAYMENT_STATUS.PAID) {
          this.contractsFilter.isPaid = status.isSelected;
        }
        if (status.value === PAYMENT_STATUS.NOT_PAID) {
          this.contractsFilter.isNotPaid = status.isSelected;
        }
        if (status.value === PAYMENT_STATUS.OVERDUE) {
          this.contractsFilter.isOverdue = status.isSelected;
        }
      });
      if (this.terminationUntilDate) this.contractsFilter.terminationUntilDate = this.terminationUntilDate;

      let count = 0;

      for (let [key, value] of Object.entries(this.contractsFilter)) {
        if (Array.isArray(value)) {
          count += value.length;
        } else if (typeof value === 'boolean' && key !== 'activeContracts' && value === true) {
          count++;
        } else if (key === 'terminationUntilDate' && value !== null && value !== undefined) {
          count++;
        }
      }
      this._activeModal.close(isEqual(this.contractsFilter, this.initialFilterState) ? false : count);
    } else {
      this._activeModal.close(false);
    }
  }

  resetFilterHandler() {
    this.contractsFilter.isOverdue = undefined;
    this.contractsFilter.isPaid = undefined;
    this.contractsFilter.isNotPaid = undefined;
    this.contractsFilter.contractAssetIds = [];
    this.contractsFilter.contractTypeCodes = [];
    this.contractsFilter.terminationUntilDate = null;
    this.contractsFilter.partnerExternalEntityId = this.partnerExternalEntityId;
    this._activeModal.close(isEqual(this.contractsFilter, this.initialFilterState) ? false : 0);
  }

  selectAssetHandler(assetId: string) {
    const contract = this.assets.find(x => x.assetId == assetId);
    if (contract) {
      contract.isSelected = !contract.isSelected;
    }
  }

  onSearchTextChange(value: string) {
    this.searchQuery$.next(value);
  }

  switchContractActiveStateHandler() {
    this.contractsFilter.activeContracts = !this.contractsFilter.activeContracts;
    this.initialFilterState.activeContracts = !this.initialFilterState.activeContracts;
    this.loadData(this.assetSearchText);
  }
}
